/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCompress, faExpand, faGrid, faList } from "@fortawesome/pro-regular-svg-icons";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Table
} from "reactstrap";
import { listFacilityLocations } from "../../../actions/FacilityLocations/actions";
import { applyDisplayFilterFields } from "../../../actions/Tenants/config/applyDisplayFilter";
import useConfig, { useTenantFormOptions } from "../../../actions/Tenants/config/configHook";
import { useFields } from "../../../hooks";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { isFilledArray } from "../../../utils";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import SingleSelect, { OptionType } from "../../Forms/SingleSelect";
import { Button } from "../../Helper/Button";
import GroupByModal, { useGroupBy } from "../../Helper/GroupByModal";
import { Loading } from "../../Helper/Loading";
import { TableCell, TableHeaderCell } from "../../Helper/Table";
import FullPageLayout from "../Layout/FullPageLayout";
import { LOCATION_TYPES } from "../Tenants/TenantEditFacilityLocationsForm";
import { LIST_OR_GRID } from "./hooks/useRipeningRoom";
import RipeWiseBreadcrumbs from "./RipeningRoomBreadcrumbs";
import { useSignalRollup } from "../Signals/signalHooks";
import { RoomSignalDot, signalsCellPath } from "../Signals/components/RoomSignalDot";
import RipeningRoomStatusText from "./RipeningRoom/RipeningRoomStatusText";

// Narrowest a room card may get in the grid view; also the grid's track size,
// so the two can't disagree and force the row past the viewport.
const ROOM_CARD_MIN_WIDTH = "23rem";
// Space kept clear on the first row for the signal dot floating above it.
const SIGNAL_DOT_GUTTER = "1.25rem";

const RipeningRoomTableWrapped = () => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    return (
        <FullPageLayout
            fluidContainer={isFullscreen}
            title="Ripening Rooms"
            breadcrumbs={<RipeWiseBreadcrumbs currentLocation={null} />}
        >
            <RipeningRoomTable isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
        </FullPageLayout>
    );
};

export default RipeningRoomTableWrapped;

const aggregateAttributes = (data, group_keys) => {
    const grouped = data.reduce((acc, item) => {
        const key = group_keys.map((k) => item[k] ?? "").join("::");

        if (!acc[key]) {
            acc[key] = {
                ...item,
                pallets: 0,
                boxes: 0,
            };
        }

        acc[key].pallets += item.pallets || 0;
        acc[key].boxes += item.boxes || 0;

        return acc;
    }, {});

    return Object.values(grouped);
};

// function RipeningRoomTotalsModal({ isOpen, toggle, groupByProps }) {
//     const ripening_cells = useSelector((state: any) => state.facilityLocations.locations);

//     if (!isFilledArray(ripening_cells)) return null;

//     return (
//         <Modal isOpen={isOpen} toggle={toggle} size="lg">
//             <ModalHeader toggle={toggle}>
//                 Ripening Room Totals
//             </ModalHeader>
//             <ModalBody>
//                 <div className="">
//                     <RipeningRoomTotals groupByProps={groupByProps} />
//                 </div>
//             </ModalBody>
//             <ModalFooter>
//                 <Button color="secondary" onClick={toggle}>
//                     Close
//                 </Button>
//             </ModalFooter>
//         </Modal>
//     );
// }


function RipeningRoomTotals({ groupByProps }) {
    const ripening_cells = useSelector((state: any) => state.facilityLocations.locations);

    if (!isFilledArray(ripening_cells)) return null;

    const allAttributes = ripening_cells.flatMap((i) => i.aggregated_layer_attributes).filter(Boolean);
    const aggregated = aggregateAttributes(allAttributes, groupByProps.groupByArray);
    return <AggregatedLayerAttributesBody data={aggregated} groupByProps={groupByProps} />;


}

// DataTable component for displaying cells
export function RipeningRoomTable({ isFullscreen, setIsFullscreen }) {
    const dispatch = useDispatch();
    const config = useConfig();
    // TODO: replace by tanstack query filter, so we can remove the reducer
    const filter = useSelector((state: any) => state.facilityLocations.filter);
    const ripening_cells = useSelector((state: any) => state.facilityLocations.locations);
    const listIsLoading = useSelector((state: any) => state.facilityLocations.isLoading);

    const form_options = useTenantFormOptions();
    const [searchParams] = useSearchParams();
    const [listOrGrid, setListOrGrid] = useState(LIST_OR_GRID.LIST);
    const { applyDisplayFilterLayerFields } = useFields();
    // const [isTotalModalOpen, setIsTotalModalOpen] = useState(false);

    const group_by_fields = applyDisplayFilterFields(config?.root_config?.ripening_group_by_fields);
    const groupByProps = useGroupBy(group_by_fields, null);

    const { groupBy } = groupByProps;

    const fruit_type = searchParams.get("fruit_type");

    useEffect(() => {
        if (config?.root_config?.ripening_room_overview_fields_default_view) {
            setListOrGrid(config?.root_config?.ripening_room_overview_fields_default_view);
        }
    }, [config?.root_config?.ripening_room_overview_fields_default_view]);

    useEffect(() => {
        dispatch(listFacilityLocations({ with_meta: true, layer_group_by_attributes: `fruit_type${groupBy ? `,${groupBy}` : ""}`, type: LOCATION_TYPES.RIPENING_ROOM, ...filter, limit: 1000 }));
    }, [groupBy]); // TODO: add debounce here

    const { navigateTo } = useNavigateFrom();

    const fields = applyDisplayFilterLayerFields(config?.root_config?.ripening_room_overview_fields, {
        fruit_type
    });

    const filtered_ripening_cells = useMemo(() => {
        return ripening_cells.map((i) => ({ ...i, fruit_type: i.aggregated_layer_attributes?.find((j) => j.fruit_type)?.fruit_type })) // * What is the first fruit type in the room?
            .map((i) => {
                // Check if cell has any fruit_type in aggregated_layer_attributes
                const hasAnyFruitType = i.aggregated_layer_attributes?.some((j) => j.fruit_type) ?? false;

                let has_fruit_type: boolean;
                if (fruit_type === "all") {
                    // Show all cells
                    has_fruit_type = true;
                } else if (fruit_type === "none") {
                    // Show only cells with no fruit_type
                    has_fruit_type = !hasAnyFruitType;
                } else if (!fruit_type) {
                    // No filter selected, show all
                    has_fruit_type = true;
                } else {
                    // Filter by specific fruit_type
                    has_fruit_type = i.aggregated_layer_attributes?.some((j) => j.fruit_type === fruit_type) ?? false;
                }

                return { ...i, has_fruit_type };
            })
            .map((i) => ({ ...i, style: css`opacity: ${i.has_fruit_type ? 1 : 0.5};background-color: ${i.has_fruit_type ? "transparent" : "#d2d2d2 !important"};` })); // * Hightlight rooms with this fruit_tye
    }, [ripening_cells, fruit_type]);

    // Per-cell signal rollup (traffic light) for the visible rooms — only when
    // the tenant has the Signals feature enabled (EFQ-4145). Disabled ⇒ no
    // rollup fetch and every RoomSignalDot renders null (undefined colour).
    const signalsEnabled = !!config?.root_config?.ripening_room_enable_signals;
    const locationIds = useMemo(
        () => filtered_ripening_cells.map((c) => c.location_id).filter(Boolean),
        [filtered_ripening_cells],
    );
    const { data: rollup } = useSignalRollup(signalsEnabled ? locationIds : []);
    // * Rooms show their live status once any room of this client is linked to its ripening room.
    const clientHasLinkedRooms = useMemo(
        () => !!config?.root_config?.ripening_room_enable_ripewise && (ripening_cells || []).some((c) => !!c?.gateway && !!c?.controller_room),
        [ripening_cells, config?.root_config?.ripening_room_enable_ripewise],
    );

    if (fields.length === 0 && !listIsLoading) {
        return <Alert color="info" className="text-center">
            No ripening room fields are configured. Please configure the fields in the tenant settings.
        </Alert>;
    }
    const values = (c) => fields.map((i, index) => <TableCell key={index}>{i.set_value({
        config,
        ripeningRoom: c,
        form_options
    }).display_component()}</TableCell>);

    const renderHeader = () => {
        return [
            <TableHeaderCell key="signals-header" />,
            ...(clientHasLinkedRooms ? [<TableHeaderCell key="status-header">Status</TableHeaderCell>] : []),
            ...fields.map((field, index) => (
                <TableHeaderCell key={index} >{field.label}</TableHeaderCell>
            )),
        ];
    };

    const isLoading = !ripening_cells?.length && listIsLoading;

    // * commented out some divs to place future filters
    return (
        <div className="pt-3">
            <div className="d-flex justify-content-between">
                <div>
                    <div className="d-flex">
                        <div className="me-2">
                            <Button
                                color="light"
                                onClick={() => setListOrGrid((prev:string) => (prev === LIST_OR_GRID.LIST ? LIST_OR_GRID.GRID : LIST_OR_GRID.LIST))}
                                icon={listOrGrid === LIST_OR_GRID.LIST ? faGrid : faList}
                            >
                                {listOrGrid === LIST_OR_GRID.LIST ? "Grid" : "List"}
                            </Button>
                        </div>
                        <div className="me-2">
                            <GroupByModal groupByProps={groupByProps} extraContent={<RipeningRoomTotals groupByProps={groupByProps} />} />
                        </div>
                        <div className="me-2">
                            <Button
                                color="light"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                icon={isFullscreen ? faCompress : faExpand}
                            >
                                {isFullscreen ? "Compress" : "Expand"}
                            </Button>
                        </div>
                        <div className="me-2">
                            <FruitSelector extraOptions={[{ label: "All", value: "all" }, { label: "Empty", value: "none" }]} />
                        </div>
                    </div>
                </div>
                <div>
                    {/* <ScanToMoveLayerModal /> */}
                    {/* <ScanToCheckModal/> */}
                </div>
            </div>


            <div className="pt-4 d-flex flex-column" css={css`min-height: 50vh;`} >
                {isLoading && <Loading />}
                {!isLoading && <div >
                    { listOrGrid === LIST_OR_GRID.LIST && <div css={css`border-radius: 8px; overflow: hidden;`} className="bg-body border">
                        <Table size="sm" responsive={true} borderless={true}>
                            <thead>
                                <tr>{renderHeader()}</tr>
                            </thead>
                            <tbody>
                                {filtered_ripening_cells.filter((i) => i.has_fruit_type).map((i, index) => (
                                    <tr key={index} className="clickable" css={i.style} title={i.location_id} onClick={() => navigateTo(`/ripening-room/${i.location_id}`)}>
                                        <TableCell><RoomSignalDot color={rollup?.[i.location_id]} onNavigate={() => navigateTo(signalsCellPath(i.location_id))} /></TableCell>
                                        {clientHasLinkedRooms && <TableCell><RipeningRoomStatusText location={i} clientHasLinkedRooms /></TableCell>}
                                        {values(i)}
                                    </tr>
                                ))}
                            </tbody>
                        </Table >
                    </div>
                    }

                    {listOrGrid === LIST_OR_GRID.GRID && (

                        <div className=""
                            css={{
                                display: "grid",
                                // A fixed track count is only used when the tenant asks for one.
                                // Cards are min-width: 23rem, so the old default of 9 tracks was
                                // ~207rem wide — always past the viewport, so rooms never wrapped
                                // and the grid scrolled sideways forever. Without an explicit
                                // setting, fit as many 23rem cards per row as the screen allows
                                // and wrap the rest.
                                gridTemplateColumns: config?.root_config?.ripening_room_number_of_rooms_per_row
                                    ? `repeat(${config.root_config.ripening_room_number_of_rooms_per_row}, minmax(${ROOM_CARD_MIN_WIDTH}, 1fr))`
                                    : `repeat(auto-fill, minmax(${ROOM_CARD_MIN_WIDTH}, 1fr))`,
                                gap: "1rem", // Spacing between items
                                overflowX: "auto",
                            }}>
                            {filtered_ripening_cells.map((c, indexx) => (
                                <div
                                    key={indexx}
                                    className="mb-3 border bg-body p-3 rounded clickable"
                                    css={[css`min-width: ${ROOM_CARD_MIN_WIDTH}; position: relative;`, c.style]}
                                    onClick={() => navigateTo(`/ripening-room/${c.location_id}`)}>
                                    <div css={css`position: absolute; top: 10px; right: 10px;`}>
                                        <RoomSignalDot color={rollup?.[c.location_id]} onNavigate={() => navigateTo(signalsCellPath(c.location_id))} />
                                    </div>
                                    <table className="w-100">
                                        <tbody>
                                            {clientHasLinkedRooms && (
                                                <tr>
                                                    <td>Status</td>
                                                    <td className="ps-2 text-end" css={css`padding-right: ${SIGNAL_DOT_GUTTER};`}>
                                                        <RipeningRoomStatusText location={c} clientHasLinkedRooms />
                                                    </td>
                                                </tr>
                                            )}
                                            {applyDisplayFilterLayerFields(config?.root_config?.ripening_room_overview_fields, {
                                                fruit_type: c.has_fruit_type ? fruit_type : c.fruit_type
                                            }).map((i, index) => (
                                                <tr key={index}>
                                                    <td>{i.label}</td>
                                                    {/* The signal dot floats over the card's top-right corner, so the
                                                        first row's right-aligned value has to yield room for it or the
                                                        two collide. Later rows sit below the dot and need no gutter. */}
                                                    <td
                                                        className="fw-bold ps-2 text-end"
                                                        css={index === 0 && !clientHasLinkedRooms ? css`padding-right: ${SIGNAL_DOT_GUTTER};` : undefined}
                                                    >
                                                        {i.set_value({
                                                            config,
                                                            ripeningRoom: c,
                                                            form_options
                                                        }).display_component()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <AggregatedLayerAttributesBody data={ c.aggregated_layer_attributes} groupByProps={groupByProps}/>
                                </div>
                            ))}
                        </div>
                    )}
                </div>}
            </div>
        </div>);
}

const AggregatedLayerAttributesBody = ({ data, groupByProps }) => {
    const config = useConfig();
    if (!isFilledArray(data)) return null;
    if (!isFilledArray(groupByProps.groupByArray)) return null;

    const group_key = "fruit_type";
    const rowgroup_keys = groupByProps.groupByArray.filter((key) => key !== group_key);
    const groupedByFruit = data.filter(Boolean).reduce((acc, item) => {
        const fruit = item?.[group_key] || "Unknown";
        acc[fruit] = acc[fruit] || [];
        acc[fruit].push(item);
        return acc;
    }, {});

    return (
        <table className="w-100 mt-2">
            {Object.entries(groupedByFruit).map(([fruit, items]: any) => {
                const totalPallets = items.reduce((sum, i) => sum + (i.pallets || 0), 0);
                const totalBoxes = items.reduce((sum, i) => sum + (i.boxes || 0), 0);

                return (
                    <React.Fragment key={fruit}>
                        <thead>
                            <tr>
                                <th colSpan={rowgroup_keys.length + 2} className="pt-3 pb-1 fw-bold text-muted">
                                    {config.get_form_option_label("fruit_type", fruit)}
                                </th>
                            </tr>
                            <tr>
                                {rowgroup_keys.map((key, index) => (
                                    <th key={index} className="text-nowrap" title={key}>
                                        {groupByProps.getGroupByLabel(key)}
                                    </th>
                                ))}
                                <th className="text-end" css={css`min-width: 5rem;`}>Pallets</th>
                                <th className="text-end" css={css`min-width: 5rem;`}>Boxes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((i, index) => (
                                <tr key={index}>
                                    {rowgroup_keys.map((key, gIndex) => (
                                        <td key={gIndex} className="text-nowrap" title={key}>
                                            {config.get_form_option_label(key, i[key])}
                                        </td>
                                    ))}
                                    <td className={`text-end ${items.length - 1 === index ? "pb-2" : ""}`} css={css`min-width: 5rem;`}>
                                        {i.pallets}
                                    </td>
                                    <td className={`text-end ${items.length - 1 === index ? "pb-2" : ""}`} css={css`min-width: 5rem;`}>
                                        {i.boxes > 0 ? i.boxes : "-"}
                                    </td>
                                </tr>
                            ))}

                            <tr>
                                <td colSpan={rowgroup_keys.length} className="text-start border-top fw-bold pt-2">
                                    Total
                                </td>
                                <td className="text-end border-top fw-bold pt-2" css={css`min-width: 5rem;`}>
                                    {totalPallets}
                                </td>
                                <td className="text-end border-top fw-bold pt-2" css={css`min-width: 5rem;`}>
                                    {totalBoxes > 0 ? totalBoxes : "-"}
                                </td>
                            </tr>
                        </tbody>
                    </React.Fragment>
                );
            })}
        </table>
    );
};


// Canot re-use the fruit type selector from layer tabls, as the fruit list is derived from the aggregated_layer_attributes
// which  comones from layer allocation to the cells
function FruitSelector({ extraOptions }: { extraOptions: OptionType[] }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const locations = useSelector((state: any) => state.facilityLocations.locations);
    const fruit_types = locations?.flatMap((i) => i.aggregated_layer_attributes?.flatMap((i) => i.fruit_type) || []) || [];
    const unique_fruit_types = Array.from(new Set(fruit_types));
    const [userPreferences] = useUserPreferences();
    const fruit_type = searchParams.get("fruit_type");
    const config = useConfig();
    const fruitOptions = unique_fruit_types.map((i) => ({
        label: config.get_form_option_label("fruit_type", i),
        value: i,
    }));
    const updateFruitType = (value: string) => {
        searchParams.set("fruit_type", value || "");
        setSearchParams(searchParams, { replace: true });
    };

    useEffect(() => {
        if (isFilledArray(fruitOptions)) {
            if (fruit_type && !extraOptions.some((i) => i.value === fruit_type) && !fruitOptions.some((i) => i.value === fruit_type)) {
                // Unknown fruit type, set to first fruit type
                updateFruitType(fruitOptions[0].value as string);
            }
            if (!fruit_type) {
                if (userPreferences.ripening_field) {
                    const fruit_type = userPreferences.ripening_field.split("__")[0];
                    updateFruitType(fruit_type);
                } else {
                    updateFruitType(fruitOptions[0].value as string);
                }
            }
        }
    }, [fruit_type, fruitOptions.length, userPreferences.ripening_field]);

    const options = [...fruitOptions, ...extraOptions];

    if (!isFilledArray(options)) return null;

    return (
        <div className="mb-n3" css={css`min-width: 12rem;`}>
            <SingleSelect
                options={options as OptionType[]}
                field="fruit_type"
                isClearable={false}
                post_value={fruit_type || ""}
                setValue={(_, value) => updateFruitType(value as string)}
            />
        </div>
    );
}
